import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';
import { authTokens } from '@/services/authTokens';
import { useAuthStore } from '@/stores/authStore';

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const hydrate = useAuthStore(s => s.hydrateFromTokens);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const valid = token.length > 0 && password.length >= 8 && password === confirm;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setLoading(true);
    try {
      const resp = await apiClient.acceptInvite(token, password);
      const data = resp.data ?? {};
      authTokens.setTokens({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: data.expires_at,
        refreshExpiresAt: data.refresh_expires_at,
      });
      hydrate();
      toast.success('Convite aceito. Bem-vindo ao MyShow!');
      navigate('/dashboard');
    } catch (err: any) {
      const code = err?.response?.data?.error;
      if (code === 'invalid_or_expired_token') toast.error('Convite expirado ou inválido. Peça pro administrador reenviar.');
      else if (code === 'weak_password') toast.error('A senha precisa ter pelo menos 8 caracteres.');
      else toast.error('Falha ao aceitar convite.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Aceitar convite</h1>
          <p className="mt-2 text-muted-foreground">Defina sua senha para entrar na conta MyShow que te convidaram.</p>
        </div>

        {!token ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900">
            Link inválido — o token de convite não foi encontrado na URL.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <Input
              type="password"
              placeholder="Crie uma senha (mínimo 8 caracteres)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={8}
              autoFocus
            />
            <Input
              type="password"
              placeholder="Repita a senha"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
            />
            {confirm.length > 0 && password !== confirm && (
              <p className="text-xs text-red-600">As senhas não conferem.</p>
            )}
            <Button type="submit" className="w-full" loading={loading} disabled={!valid}>
              Aceitar convite e entrar
            </Button>
          </form>
        )}

        <Link to="/login" className="text-sm text-brand-600 hover:underline block text-center">
          ← Voltar pro login
        </Link>
      </div>
    </div>
  );
}
